/**
 * Pixel art drawn in code, one logical pixel per canvas pixel. A first pass to
 * make the garden playable; the RPG Lab art can replace these piece by piece.
 */
import type { Difficulty, GrowthStage } from '@thicket/core';

type Ctx = CanvasRenderingContext2D;

const PAL: Record<string, string> = {
  k: '#2b1d14', w: '#f6f2e6', y: '#f2c12e', o: '#d9772b', O: '#f0a35c', b: '#8a5a35', B: '#4a2e1b', t: '#dcbc8e',
  g: '#4cae48', G: '#2a7030', r: '#d9434b', p: '#ef93b0', l: '#4a8fd6', L: '#2f62a8', e: '#141414', u: '#8f7fd9', n: '#b8b0a2',
};

export interface Sprite { w: number; h: number; rows: string[] }
export function S(rows: string[]): Sprite {
  const w = Math.max(...rows.map((r) => r.length));
  return { w, h: rows.length, rows: rows.map((r) => r.padEnd(w, '.')) };
}
export function spr(ctx: Ctx, s: Sprite, x: number, y: number, flip = false) {
  x = Math.round(x);
  y = Math.round(y);
  for (let j = 0; j < s.h; j++) {
    for (let i = 0; i < s.w; i++) {
      const ch = s.rows[j]![i]!;
      if (ch === '.') continue;
      ctx.fillStyle = PAL[ch]!;
      ctx.fillRect(x + (flip ? s.w - 1 - i : i), y + j, 1, 1);
    }
  }
}

/** Stable pseudo-random from a string or numbers, so each plant keeps its quirks. */
export function hash(...parts: (string | number)[]): number {
  let h = 2166136261;
  for (const c of parts.join('|')) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return h >>> 0;
}

/* ---------- decorative flowers (seed packets) ---------- */

export const FLOWERS: Record<string, Sprite> = {
  clover: S(['.......', '..w.w..', '.gGgGg.', '.gGwGg.', '..gGg..', '.gg.gg.', '...g...', '...g...']),
  daisy: S(['.w.w.w.', '..wyw..', '.wyyyw.', '..wyw..', '.w.g.w.', '...g...', '..gg...', '...g...']),
  tulip: S(['.r.r.r.', '.rprrr.', '.rrrrr.', '..rrr..', '...g...', '.g.g...', '..gg...', '...g...']),
  sunflower: S(['.y.y.y.', 'yyBbByy', '.yBBBy.', 'yyBbByy', '.y.y.y.', '...g...', '.ggg...', '...g.g.', '...ggg.', '...g...']),
};

/* ---------- wildflowers (to-dos) ---------- */

const BLOOM_COLORS: Record<Difficulty, [string, string]> = {
  trivial: ['#f6f2e6', '#f2c12e'],
  easy: ['#f2c12e', '#d9772b'],
  medium: ['#ef93b0', '#d9434b'],
  hard: ['#8f7fd9', '#f2c12e'],
};

export function drawBud(ctx: Ctx, x: number, y: number, pace: Difficulty, drooping: boolean) {
  const [petal] = BLOOM_COLORS[pace];
  ctx.fillStyle = '#2a7030';
  for (let i = 0; i < 6; i++) ctx.fillRect(x + (drooping && i > 3 ? 1 : 0), y - i, 1, 1);
  ctx.fillStyle = '#4cae48';
  ctx.fillRect(x - 2, y - 2, 2, 1);
  ctx.fillRect(x + 1, y - 3, 2, 1);
  const bx = x + (drooping ? 2 : 0);
  const by = y - (drooping ? 6 : 8);
  ctx.fillStyle = '#4cae48';
  ctx.fillRect(bx - 1, by, 3, 3);
  ctx.fillStyle = petal;
  ctx.fillRect(bx, by - 1, 1, 2);
}

export function drawBloom(ctx: Ctx, x: number, y: number, pace: Difficulty, daysLeft: number, tick: number) {
  const [petal, center] = BLOOM_COLORS[pace];
  const size = pace === 'hard' ? 3 : pace === 'medium' ? 2 : 2;
  ctx.fillStyle = '#2a7030';
  for (let i = 0; i < 7; i++) ctx.fillRect(x, y - i, 1, 1);
  ctx.fillStyle = '#4cae48';
  ctx.fillRect(x - 2, y - 3, 2, 1);
  ctx.fillRect(x + 1, y - 4, 2, 1);
  const cy = y - 9;
  // Fading flowers tilt and lose a petal or two in their last days.
  const wilting = daysLeft <= 2;
  ctx.fillStyle = petal;
  for (let dy = -size; dy <= size; dy++) {
    for (let dx = -size; dx <= size; dx++) {
      if (dx * dx + dy * dy > size * size + 1) continue;
      if (wilting && (dx + dy + daysLeft) % 3 === 0) continue;
      ctx.fillRect(x + dx, cy + dy, 1, 1);
    }
  }
  ctx.fillStyle = center;
  ctx.fillRect(x, cy, 1, 1);
  if (!wilting && (tick + hash(x, y)) % 40 < 2) {
    ctx.fillStyle = '#fffbe0';
    ctx.fillRect(x + size, cy - size, 1, 1);
  }
}

/* ---------- trees (dailies) ---------- */

const SIZES: Record<GrowthStage, { r: number; trunk: number }> = {
  seed: { r: 0, trunk: 0 }, small: { r: 4, trunk: 4 }, medium: { r: 7, trunk: 7 }, large: { r: 10, trunk: 10 }, mega: { r: 13, trunk: 13 },
};

const SPECIES: Record<string, { leaf: [string, string, string]; bark: [string, string] }> = {
  birch: { leaf: ['#a9d35a', '#cbe77f', '#6f9a36'], bark: ['#efece2', '#3a3a3a'] },
  maple: { leaf: ['#d9542b', '#f0884a', '#9c3320'], bark: ['#6b4527', '#4a2e1b'] },
  oak: { leaf: ['#3f8f3a', '#69b74e', '#2a6a2c'], bark: ['#6b4527', '#4a2e1b'] },
  pine: { leaf: ['#2f6e45', '#4c8f5a', '#1d4a2d'], bark: ['#5c3a22', '#3a2414'] },
};

/** Treetop anchors, so animals know where to sit. */
export function treeTop(stage: GrowthStage, x: number, y: number) {
  const { r, trunk } = SIZES[stage];
  return { top: [x, y - trunk - r * 2 + 2] as [number, number], mid: [x, y - trunk - r + 1] as [number, number] };
}

export function drawTree(ctx: Ctx, species: string, stage: GrowthStage, sparse: boolean, x: number, y: number, seed: number, tick: number) {
  const pal = SPECIES[species] ?? SPECIES.oak!;
  const [leaf, hi, shade] = pal.leaf;
  const { r, trunk } = SIZES[stage];
  ctx.fillStyle = 'rgba(30,50,25,.3)';
  ctx.fillRect(x - 3 - r, y, 6 + r * 2, 2);

  if (stage === 'seed') {
    // A sapling: a mound, a thin stem, and a tuft in the species' colour.
    ctx.fillStyle = '#5a3c25';
    ctx.fillRect(x - 3, y - 1, 7, 2);
    ctx.fillStyle = '#7a5434';
    ctx.fillRect(x - 2, y - 2, 5, 1);
    ctx.fillStyle = pal.bark[0] === '#efece2' ? '#d8d2c0' : '#6b4527';
    ctx.fillRect(x, y - 7, 1, 5);
    for (const [dx, dy, c] of [[-1, -8, leaf], [0, -9, hi], [1, -8, leaf], [-2, -7, shade], [2, -7, leaf], [0, -8, leaf], [-1, -6, hi], [1, -5, leaf], [2, -6, shade]] as const) {
      if (sparse && (dx + dy) % 2) continue;
      ctx.fillStyle = c;
      ctx.fillRect(x + dx, y + dy, 1, 1);
    }
    return;
  }

  // Trunk
  const tw = stage === 'small' ? 1 : stage === 'medium' ? 2 : 3;
  ctx.fillStyle = '#2b1d14';
  ctx.fillRect(x - Math.floor(tw / 2) - 1, y - trunk, tw + 2, trunk + 1);
  ctx.fillStyle = pal.bark[0];
  ctx.fillRect(x - Math.floor(tw / 2), y - trunk, tw, trunk);
  if (species === 'birch') {
    ctx.fillStyle = pal.bark[1];
    for (let i = 2; i < trunk; i += 3) ctx.fillRect(x - Math.floor(tw / 2) + ((i + seed) % tw), y - i, 1, 1);
  }

  const cy = y - trunk - r + 2;
  const holes = (px: number, py: number) => sparse && hash(px, py, seed) % 5 < 3;
  const put = (px: number, py: number, color: string) => {
    if (holes(px, py)) return;
    ctx.fillStyle = color;
    ctx.fillRect(px, py, 1, 1);
  };

  if (species === 'pine') {
    const height = r * 2 + 4;
    for (let j = 0; j < height; j++) {
      const layer = j % Math.max(3, Math.floor(r / 2));
      const half = Math.floor(((j + 1) / height) * (r + 1)) - (layer === 0 ? 1 : 0);
      for (let i = -half; i <= half; i++) put(x + i, y - trunk - height + 2 + j, i < -half / 3 ? hi : i > half / 2 ? shade : leaf);
    }
  } else {
    const blobs: [number, number, number][] =
      species === 'oak' && r >= 7
        ? [[-Math.floor(r / 2), 1, Math.ceil(r * 0.7)], [Math.floor(r / 2), 1, Math.ceil(r * 0.7)], [0, -Math.floor(r / 3), r]]
        : [[0, 0, r]];
    for (const [ox, oy, br] of blobs) {
      for (let dy = -br; dy <= br; dy++) {
        for (let dx = -br; dx <= br; dx++) {
          if (dx * dx + dy * dy > br * br + br * 0.6) continue;
          const tone = dx + dy > br * 0.55 ? shade : dx + dy < -br * 0.6 ? hi : leaf;
          put(x + ox + dx, cy + oy + dy, tone);
        }
      }
    }
  }

  // Sparse trees show a few bare branches; mega trees glow with fireflies.
  if (sparse) {
    ctx.fillStyle = pal.bark[1];
    for (let i = 1; i < r; i += 2) {
      ctx.fillRect(x + i, cy - Math.floor(i / 2), 1, 1);
      ctx.fillRect(x - i, cy - Math.floor(i / 2) + 1, 1, 1);
    }
  }
  if (stage === 'mega') {
    for (let k = 0; k < 4; k++) {
      const phase = (tick / 6 + k * 7 + seed) % 28;
      if (phase > 18) continue;
      ctx.fillStyle = phase % 6 < 3 ? '#fff4a8' : '#f2c12e';
      ctx.fillRect(x + Math.round(Math.sin(phase / 3 + k) * (r + 3)), cy + Math.round(Math.cos(phase / 4 + k * 2) * r), 1, 1);
    }
  }
}

/* ---------- raised-bed plants (habits) and weeds ---------- */

export function drawBedPlant(ctx: Ctx, stage: GrowthStage, x: number, y: number, seed: number) {
  const leaf = '#4cae48';
  const dark = '#2a7030';
  const size = { seed: 2, small: 3, medium: 4, large: 5, mega: 6 }[stage];
  ctx.fillStyle = dark;
  ctx.fillRect(x, y - size * 2, 1, size * 2);
  for (let i = 0; i < size; i++) {
    ctx.fillStyle = i % 2 ? leaf : dark;
    ctx.fillRect(x - 1 - i % 2, y - 2 - i * 2, 1 + (i % 2), 1);
    ctx.fillRect(x + 1, y - 3 - i * 2, 1 + ((i + 1) % 2), 1);
  }
  if (stage === 'large' || stage === 'mega') {
    ctx.fillStyle = ['#ef93b0', '#f2c12e', '#8f7fd9'][seed % 3]!;
    ctx.fillRect(x, y - size * 2 - 1, 1, 1);
    if (stage === 'mega') {
      ctx.fillRect(x - 2, y - size * 2 + 1, 1, 1);
      ctx.fillRect(x + 2, y - size * 2 + 2, 1, 1);
    }
  }
}

export function drawWeed(ctx: Ctx, x: number, y: number) {
  ctx.fillStyle = '#7a8f3a';
  ctx.fillRect(x, y - 3, 1, 3);
  ctx.fillRect(x - 1, y - 2, 1, 1);
  ctx.fillRect(x + 1, y - 1, 1, 1);
  ctx.fillStyle = '#e0d36a';
  ctx.fillRect(x, y - 4, 1, 1);
}

/* ---------- animals ---------- */

export const ANIMAL_SPRITES: Record<string, Sprite> = {
  songbird: S(['..kk..', '.klek.', 'klllkO', '.kLlk.', '..kk..']),
  squirrel: S(['.k....k.', 'kok..kok', 'kooooook', 'koeooeok', '.koOOok.', '.k.kk.k.']),
  owl: S(['k......k', 'kbk..kbk', 'kbbbbbbk', 'kwwbbwwk', 'kwebbewk', 'kbbyybbk', 'kbttttbk', '.kbttbk.', '..y..y..']),
  bumblebee: S(['.ww..', '.kkkk', 'kykBk', '.kkkk']),
  toad: S(['.k..k..', 'kgkkgk.', 'kgegegk', 'kgggggk', '.kG.Gk.']),
  butterfly: S(['k...k', 'kOkOk', '.kok.', 'kOkOk', 'k...k']),
};
